import type { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";

/**
 * `ICommonSliceToolModel` — web port of Unity's `ICommonSliceToolModel`
 * (`Models/Tools/CommonSliceToolModel.cs`), the owner of the per-scene
 * persisted "Cutting Plane".
 *
 * Web shape differences (documented, deliberate):
 *   - Unity owns a live `GameObject` and re-derives state per frame; the web
 *     model is headless — persisted state lives on the active scene's
 *     `SceneInfo.cuttingPlane` and the model only layers a transient gizmo
 *     preview on top. `SlicePlaneRenderer` (runtime) draws + clips from it.
 *   - `ClearCommonSlice` / `CreateCommonSlice(Transform)` (the VR hand-plane
 *     commit path through `SliceColliderModel`) are not ported — there is no
 *     hand plane on web. `DeleteCommonSlice` folds into
 *     `setVisibleAsync(false)` (persist Visible=false, keep Exist — Unity's
 *     hierarchy-eye semantics).
 *   - Mutations are Promise-returning instead of Unity's fire-and-forget
 *     `async void` so callers can surface storage failures.
 *
 * Pose contract (`CuttingPlanePose`) is the PERSISTED space: scene-root
 * meters + Unity ZXY euler degrees WITHOUT the 180°-about-Y gizmo offset.
 * Kept half-space is `dot(p - position, forwardOfEuler(euler)) >= 0`
 * (see `models/tools/cuttingPlaneMath.ts`).
 */

export type CuttingPlaneAxisMode = "free" | "x" | "y" | "z";

export interface CuttingPlanePose {
  readonly position: Vec3;
  readonly euler: Vec3;
}

export interface ICommonSliceToolModel {
  /** Unity `CommonSliceChanged` — true on create/show, false after remove. */
  readonly onCommonSliceChanged: Emitter<boolean>;
  /** Unity `AxisChanged`, extended with which mode was chosen. */
  readonly onAxisChanged: Emitter<CuttingPlaneAxisMode>;
  /** Any pose / visibility / part-color change (renderer + inspector refresh). */
  readonly onStateChanged: Emitter<void>;

  /** Mirrors `SceneInfo.cuttingPlane.exist` of the active scene. */
  readonly exists: boolean;
  /** Mirrors `SceneInfo.cuttingPlane.visible` of the active scene. */
  readonly visible: boolean;
  /** Live pose: transient gizmo preview when dragging, else persisted. */
  readonly pose: CuttingPlanePose;
  readonly usePartColor: boolean;
  /** Local-only axis restriction; always reopens in `"free"` like Unity. */
  readonly axisMode: CuttingPlaneAxisMode;
  /** Unity `IsSliceGizmoEnabled` — true while the slice tool drives the plane. */
  isSliceGizmoEnabled: boolean;

  /**
   * Unity `InitialCommonSlice()`: create the plane at `anchor` with a
   * horizontal cut (visual forward = scene-root up) when missing, then
   * show + persist Visible = Exist = true. Existing planes are just
   * re-shown.
   */
  initialCommonSliceAsync(anchor?: Vec3): Promise<void>;
  /** Gizmo-drag live pose (no persistence; renderer follows immediately). */
  previewPose(pose: CuttingPlanePose): void;
  /** Persist the current previewed pose (gizmo drag end). */
  commitPoseAsync(): Promise<void>;
  /** Inspector field commit: apply + persist an explicit pose. */
  setPoseAsync(pose: CuttingPlanePose): Promise<void>;
  /**
   * Eye toggle. `false` = Unity `DeleteCommonSlice` (persist Visible=false,
   * keep Exist); `true` persists Visible=true with the current pose.
   */
  setVisibleAsync(visible: boolean): Promise<void>;
  /** Unity `RemoveCommonSlice()`: persist Visible = Exist = false. */
  removeCommonSliceAsync(): Promise<void>;
  /** Unity `FlipCommonSlice()`: negate the plane forward (persisted). */
  flipCommonSliceAsync(): Promise<void>;
  /** Unity `FlipCommonSliceto{X,Y,Z}Axis()`: snap visual forward to a root axis. */
  flipToAxisAsync(axis: "x" | "y" | "z"): Promise<void>;
  /** Unity `FreeMode()`. */
  freeMode(): void;
  /** Unity `BaseSceneModel.UpdateUsingPartColor` (same persistence batch). */
  setUsePartColorAsync(value: boolean): Promise<void>;

  dispose(): void;
}

export const COMMON_SLICE_TOOL_MODEL = Symbol.for("Campfire.CommonSliceToolModel");
